import React, { useEffect, useRef, useState, memo } from "react";
import Tooltip from "../../../../Components/Tooltip";
import { FaAngleDown } from "react-icons/fa";
import CashFlow from "./CashFlowGraph";
import { IoMdInformationCircleOutline } from "react-icons/io";
import useAuthStore from "../../../../store/useAuthStore";

const dropdownOptions = [
  "This Fiscal Year",
  "Previous Fiscal Year",
  "Last 12 Months",
  "Last 6 Months",
];

function CashFlowCard() {
  const [isOpenCashFlow, setIsOpenCashFlow] = useState(false);
  const [yearDropDown, setYearDropDown] = useState(dropdownOptions[0]);
  const [combinedData, setCombinedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const dropdownRefCashFlow = useRef(null);
  const { token } = useAuthStore();

  const toggleDropdownCashFlow = () => setIsOpenCashFlow(prev => !prev);

  const closeDropdownCashFlow = (e) => {
    if (
      dropdownRefCashFlow.current &&
      !dropdownRefCashFlow.current.contains(e.target)
    ) {
      setIsOpenCashFlow(false);
    }
  };

  const handleSelectionCashFlow = (name) => {
    setYearDropDown(name);
    setIsOpenCashFlow(false);
  };

  // Fetch data for the dashboard card
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const today = new Date();
        const currentYear = today.getFullYear();
        const lastYear = currentYear - 1;

        const getStartOfFiscalYear = (year) => new Date(year, 0, 1);
        const getEndOfFiscalYear = (year) => new Date(year + 1, 0, 0);

        const getStartOf12MonthsAgo = () => {
          const d = new Date(today);
          d.setMonth(today.getMonth() - 11, 1);
          return d;
        };

        const getStartOf6MonthsAgo = () => {
          const d = new Date(today);
          d.setMonth(today.getMonth() - 5, 1);
          return d;
        };

        const ranges = {
          "This Fiscal Year": {
            start: getStartOfFiscalYear(currentYear),
            end: today,
          },
          "Previous Fiscal Year": {
            start: getStartOfFiscalYear(lastYear),
            end: getEndOfFiscalYear(lastYear),
          },
          "Last 12 Months": {
            start: getStartOf12MonthsAgo(),
            end: today,
          },
          "Last 6 Months": {
            start: getStartOf6MonthsAgo(),
            end: today,
          },
        };

        let startStr = "";
        let endStr = "";

        if (ranges[yearDropDown]) {
          const { start, end } = ranges[yearDropDown];
          startStr = start.toISOString().split("T")[0];
          endStr = end.toISOString().split("T")[0];
        }

        const fetchOptions = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const commonParams = `startDate=${startStr}&endDate=${endStr}&limit=-1`;

        const [incomeResponse, expenseResponse] = await Promise.all([
          fetch(`/api/getallIncomes?${commonParams}`, fetchOptions),
          fetch(`/api/getallexpense?${commonParams}`, fetchOptions)
        ]);

        if (!incomeResponse.ok || !expenseResponse.ok) {
          throw new Error("Failed to fetch data");
        }

        const incomeResult = await incomeResponse.json();
        const expenseResult = await expenseResponse.json();

        const combined = [
          ...incomeResult.incomes.map(income => ({
            ...income,
            type: "income",
          })),
          ...expenseResult.expenses.map(expense => ({
            ...expense,
            type: "expense",
          })),
        ];

        setCombinedData(combined);
        setError("");
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchData();
    }
  }, [token, yearDropDown]);

  useEffect(() => {
    if (isOpenCashFlow) {
      document.addEventListener("click", closeDropdownCashFlow);
    }
    return () => {
      document.removeEventListener("click", closeDropdownCashFlow);
    };
  }, [isOpenCashFlow]);

  if (error) {
    return (
      <div className="bg-white flex-1 rounded-lg shadow-md border border-gray-200 p-4">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-white flex-1 rounded-lg shadow-md border border-gray-200">
      <div className="flex items-center justify-between w-full p-4 border-b bg-white rounded-t-lg relative md:p-4 z-10">
        <div className="flex items-center gap-x-1 md:gap-x-2">
          <h4 className="text-base md:text-lg font-medium text-gray-700">
            Cash Flow
          </h4>
          <Tooltip
            tooltip="Amount of money moving in and out of your business"
            position="bottom"
          >
            <IoMdInformationCircleOutline className="text-gray-500 text-xl cursor-pointer" />
          </Tooltip>
        </div>
        <div className="relative inline-block" ref={dropdownRefCashFlow}>
          <div
            className="flex items-center bg-white px-2 py-1 rounded focus:outline-none cursor-pointer"
            onClick={toggleDropdownCashFlow}
          >
            <button className="w-[140px] text-right">{yearDropDown}</button>
            <FaAngleDown className="text-primary" />
          </div>
          <div
            className={`absolute top-full p-2 left-0 mt-2 w-48 bg-gray-100 shadow-lg rounded-xl z-10 ${isOpenCashFlow ? "block" : "hidden"
              }`}
          >
            {dropdownOptions.map(option => (
              <div
                key={option}
                className="px-4 py-2 hover:bg-primary cursor-pointer hover:rounded-xl hover:text-white"
                onClick={() => handleSelectionCashFlow(option)}
              >
                {option}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="p-2">
        <CashFlow
          yearDropDown={yearDropDown}
          combinedData={combinedData}
          loading={loading}
        />
      </div>
    </div>
  );
}

export default memo(CashFlowCard);